import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import DownloadIcon from 'mdi-react/DownloadIcon';
import LinkVariantIcon from 'mdi-react/LinkVariantIcon';
import QrcodeEditIcon from 'mdi-react/QrcodeEditIcon';
import TrophyOutlineIcon from 'mdi-react/TrophyOutlineIcon';
import CalendarIcon from 'mdi-react/CalendarIcon';
import { Widget } from './shared';
import t, { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';
import Api from '../../../util/api';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import { QRCodeGenerator } from '../../../shared/components/QrCodeGenerator';

const greetingKey = (hour) => {
  if (hour >= 5 && hour < 12) return 'goodMorning';
  if (hour >= 12 && hour < 18) return 'goodAfternoon';
  return 'goodEvening';
};

// Match times are stored as local wall-clock time in UTC fields - the same
// conversion the match center list uses.
const matchTime = (match) => moment.utc(match.matchStart).local(true);

const teamName = (match, type) =>
  match.isCustom ? match.extraData?.[type] : match[type]?.name;

const soonest = (items) => {
  const now = moment();
  return items
    .filter((item) => item.time.isValid() && item.time.isAfter(now))
    .sort((a, b) => a.time.valueOf() - b.time.valueOf())[0];
};

const findNextMatch = (matches) =>
  soonest(
    (matches || []).map((match) => ({
      type: 'match',
      time: matchTime(match),
      label: `${teamName(match, 'homeTeam') || ''} – ${
        teamName(match, 'awayTeam') || ''
      }`,
      link: `/matches/upcoming/edit/${match.id}`,
    }))
  );

const findNextEvent = (events) =>
  soonest(
    (events || []).map((event) => ({
      type: 'event',
      time: moment(event.startDate),
      label: event.title,
      link: `/events/edit/${event.id}`,
    }))
  );

const WelcomeCard = () => {
  const dT = partial('DashboardPage');
  const s = partial('shared');
  const config = useConfig();
  const me = useSelector((state) => state.admin.me);
  const greeting = dT(greetingKey(new Date().getHours()));
  const appLink = `https://content.api.fangroup.io/${config.app_name}/club-home`;
  const canvasRef = useRef(null);
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const teamIds = (config.teams || []).map((team) => team.id);

  // Same query key as the upcoming-events widget, so the two share one
  // request when both are on the dashboard.
  const { data: eventsData, isFetched: eventsFetched } = useQuery({
    queryKey: ['upcoming-events'],
    queryFn: () => Api.events.upcomingEvents(),
  });
  const { data: matchesData, isFetched: matchesFetched } = useQuery({
    queryKey: ['welcome-next-matches', teamIds],
    queryFn: () => Api.matchCenter.getUpcomingMatches(1, 5, teamIds, false),
    enabled: teamIds.length > 0,
  });
  // Held back until both requests have settled (success or failure), so the
  // lines don't appear one after the other and shift the card.
  const nextUpReady = eventsFetched && (matchesFetched || teamIds.length === 0);
  const nextUp = nextUpReady
    ? [
        findNextMatch(matchesData?.data?.matches),
        findNextEvent(eventsData?.data?.events),
      ].filter(Boolean)
    : [];

  useEffect(() => {
    if (canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, appLink, { width: 72, margin: 1 });
    }
  }, [appLink]);

  // The preview is only 72px, so the download is rendered separately at a
  // size that still scans when printed.
  const downloadQr = async () => {
    const dataUrl = await QRCode.toDataURL(appLink, { width: 512, margin: 2 });
    const link = document.createElement('a');
    link.download = 'app-link-qr.png';
    link.href = dataUrl;
    link.click();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(appLink);
      toast.success(s('linkCopied'));
    } catch {
      toast.error(s('somethingWentWrong'));
    }
  };

  return (
    <Widget>
      <Modal
        isOpen={generatorOpen}
        toggle={() => setGeneratorOpen(false)}
        centered
      >
        <ModalHeader toggle={() => setGeneratorOpen(false)}>
          {t('ClubProfilePage.appLinks')}
        </ModalHeader>
        <ModalBody>
          <QRCodeGenerator defaultUrl={appLink} />
        </ModalBody>
      </Modal>
      <div className="welcome-card">
        <div className="welcome-card__main">
          <h3 className="welcome-card__greeting">
            {me?.firstname ? `${greeting}, ${me.firstname}` : greeting}
          </h3>
          <div className="welcome-card__subtitle">
            {dT('welcomeBack', { club: config.club_name })}
            {' · '}
            {moment().format('dddd D MMMM')}
          </div>
          {nextUp.length > 0 && (
            <div className="welcome-card__next">
              {nextUp.map((item) => (
                <Link
                  key={item.type}
                  to={item.link}
                  className="welcome-card__next-item"
                >
                  <span className="welcome-card__next-icon">
                    {item.type === 'match' ? (
                      <TrophyOutlineIcon />
                    ) : (
                      <CalendarIcon />
                    )}
                  </span>
                  <span className="welcome-card__next-text">
                    <span className="welcome-card__next-label">
                      {dT(item.type === 'match' ? 'nextMatch' : 'nextEvent')}
                    </span>
                    <span className="welcome-card__next-title d-block">
                      {item.label}
                    </span>
                    <span className="welcome-card__next-time">
                      {item.time.format('ddd D MMM, HH:mm')}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="welcome-card__qr">
          <canvas ref={canvasRef} width={72} height={72} />
          <div>
            <small className="welcome-card__qr-label">{dT('appLink')}</small>
            <div className="icon-actions">
              <SquareIconButton
                icon={<DownloadIcon />}
                onClick={downloadQr}
                title={dT('downloadQrCode')}
                id="welcome-download-qr"
              />
              <SquareIconButton
                icon={<LinkVariantIcon />}
                onClick={copyLink}
                title={s('copyLink')}
                id="welcome-copy-app-link"
              />
              <SquareIconButton
                icon={<QrcodeEditIcon />}
                onClick={() => setGeneratorOpen(true)}
                title={dT('otherAppLink')}
                id="welcome-other-app-link"
              />
            </div>
          </div>
        </div>
      </div>
    </Widget>
  );
};

export default WelcomeCard;
